import { UploadOutlined } from '@ant-design/icons';
import {
  CreateCommunityPostRequestModel,
  CommunityTopicResponseModel,
  TopicType,
  ProjectResponseModel,
} from '@uniquegood/realworld-admin-interface';
import { Button, Form, Input, Modal, ModalProps, Select, Upload, message, Spin, Avatar } from 'antd';
import React from 'react';
import { debounce } from 'es-toolkit';
import { fileApi, communityPostApi, projectApi } from '@src/apis/admin';

function ProjectOptionLabel({ project }: { project: ProjectResponseModel }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <Avatar
        src={project.verticalImageUrl}
        size={32}
        shape="square"
        style={{ flexShrink: 0 }}
      >
        {project.name?.[0]}
      </Avatar>
      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
        <span style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {project.name}
        </span>
        <span style={{ fontSize: 11, color: '#999' }}>{project.id}</span>
      </div>
    </div>
  );
}

interface CreatePostModalProps {
  modalData: ModalProps;
  closeModal: () => unknown;
  refetch: () => unknown;
  topics: CommunityTopicResponseModel[] | undefined;
  topicsLoading: boolean;
}

interface FormValues {
  topicId: string;
  title: string;
  content: string;
  images?: { fileList: Array<{ originFileObj: File }> };
  taggedProjectId?: string;
  linkedProjectIds?: string[];
}

export default function CreatePostModal({ modalData, closeModal, refetch, topics, topicsLoading }: CreatePostModalProps) {
  const [form] = Form.useForm<FormValues>();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [selectedTopicId, setSelectedTopicId] = React.useState<string | undefined>();
  const [projectOptions, setProjectOptions] = React.useState<ProjectResponseModel[]>([]);
  const [isSearchingProjects, setIsSearchingProjects] = React.useState(false);

  const selectedTopic = topics?.find((t) => t.id === selectedTopicId);
  const isRecruitmentTopic = selectedTopic?.topicType === TopicType.Recruitment;

  const searchProjects = async (keyword: string) => {
    try {
      setIsSearchingProjects(true);
      const { data } = await projectApi.getProjects(keyword || undefined, undefined, undefined, 1, 20);
      setProjectOptions(data.data.data || []);
    } catch (e) {
      console.error(e);
      setProjectOptions([]);
    } finally {
      setIsSearchingProjects(false);
    }
  };

  const debouncedSearch = React.useMemo(
    () => debounce((keyword: string) => {
      searchProjects(keyword);
    }, 300),
    []
  );

  const handleProjectSearch = (keyword: string) => {
    debouncedSearch(keyword);
  };

  const handleTopicChange = (topicId: string) => {
    setSelectedTopicId(topicId);
    form.setFieldsValue({
      taggedProjectId: undefined,
      linkedProjectIds: undefined,
    });
    setProjectOptions([]);
  };

  const handleSubmit = async (values: FormValues) => {
    try {
      setIsSubmitting(true);

      let imageIds: string[] | undefined;

      if (values.images?.fileList && values.images.fileList.length > 0) {
        imageIds = await Promise.all(
          values.images.fileList.map(async (file) => {
            const { data } = await fileApi.uploadFile(undefined, undefined, file.originFileObj);
            return data.data.id || '';
          })
        );
      }

      const requestModel: CreateCommunityPostRequestModel = {
        topicId: values.topicId,
        title: values.title,
        content: values.content,
        imageIds: imageIds || [],
        isRecruitment: isRecruitmentTopic,
        taggedProjectId: isRecruitmentTopic ? values.taggedProjectId : undefined,
        linkedProjectIds: !isRecruitmentTopic ? values.linkedProjectIds : undefined,
      };

      const { data } = await communityPostApi.postCommunityPost(undefined, requestModel);

      if (data.success) {
        message.success('게시글이 작성되었습니다.');
        form.resetFields();
        setSelectedTopicId(undefined);
        setProjectOptions([]);
        closeModal();
        await refetch();
      }
    } catch (e) {
      console.error(e);
      message.error('게시글 작성에 실패했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      {...modalData}
      title="게시글 작성"
      onOk={form.submit}
      okText="작성"
      cancelText="취소"
      confirmLoading={isSubmitting}
      width={600}
    >
      <Form
        form={form}
        onFinish={handleSubmit}
        preserve={false}
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
        validateMessages={{ required: '필수로 입력해주세요.' }}
      >
        <Form.Item name="topicId" label="토픽" rules={[{ required: true }]}>
          <Select
            placeholder="토픽을 선택해주세요."
            options={topics?.map((topic) => ({
              value: topic.id,
              label: topic.name,
            }))}
            loading={topicsLoading}
            onChange={handleTopicChange}
          />
        </Form.Item>
        <Form.Item name="title" label="제목" rules={[{ required: true }]}>
          <Input placeholder="제목을 입력해주세요." maxLength={100} showCount />
        </Form.Item>
        <Form.Item name="content" label="내용" rules={[{ required: true }]}>
          <Input.TextArea
            autoSize={{ minRows: 5, maxRows: 10 }}
            placeholder="내용을 입력해주세요."
            maxLength={5000}
            showCount
          />
        </Form.Item>
        <Form.Item name="images" label="이미지">
          <Upload
            multiple
            beforeUpload={() => false}
            listType="picture"
            accept="image/*"
          >
            <Button icon={<UploadOutlined />}>이미지 업로드</Button>
          </Upload>
        </Form.Item>

        {isRecruitmentTopic && (
          <Form.Item name="taggedProjectId" label="태그 프로젝트">
            <Select
              placeholder="프로젝트 이름을 검색해주세요."
              allowClear
              showSearch
              filterOption={false}
              onSearch={handleProjectSearch}
              notFoundContent={isSearchingProjects ? <Spin size="small" /> : '검색 결과가 없습니다.'}
              optionLabelProp="label"
            >
              {projectOptions.map((project) => (
                <Select.Option key={project.id} value={project.id} label={project.name}>
                  <ProjectOptionLabel project={project} />
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )}

        {selectedTopicId && !isRecruitmentTopic && (
          <Form.Item name="linkedProjectIds" label="링크 프로젝트">
            <Select
              mode="multiple"
              placeholder="프로젝트 이름을 검색해주세요."
              allowClear
              showSearch
              filterOption={false}
              onSearch={handleProjectSearch}
              notFoundContent={isSearchingProjects ? <Spin size="small" /> : '검색 결과가 없습니다.'}
              optionLabelProp="label"
            >
              {projectOptions.map((project) => (
                <Select.Option key={project.id} value={project.id} label={project.name}>
                  <ProjectOptionLabel project={project} />
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}
